import ErrorMessage from "components/error-message";
import { Button } from "@material-ui/core";
import useIdResource from "hooks/use-id-resource";
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { RootState, StoreError, UserInfo } from "store/reducer";
import { TastingEvent } from "store/reducers/events/reducer";
import { Result, resultsSelectors } from "store/reducers/results/reducer";
import TastingResults from "../../components/tasting-results";
import { results, users } from "../../store";
import {
  Container,
  CalculateResultsButton,
  ResultsCircularProgress,
  Section,
  ResultsContainer,
} from "./Results.styles";
import { User, usersSelectors } from "store/reducers/users/reducer";

const Results = () => {
  const { id } = useParams<{ id: string }>();

  const dispatch = useDispatch();
  const user = useSelector<RootState, UserInfo | null>((s) => s?.auth?.user);

  // Per-event results — id-resource handles the request-token staleness guard
  // so the A → B → A race that used to leave this screen stuck is gone.
  const {
    item: tastingResults,
    error: resultsError,
    showLoading: resultsShowLoading,
  } = useIdResource<Result>({
    id,
    selectors: resultsSelectors,
    load: useCallback((eventId: string) => results.loadItem(eventId), []),
  });

  // Per-event related users — same guard, separately keyed.
  const {
    item: relatedUsersItem,
    error: usersError,
    showLoading: usersShowLoading,
  } = useIdResource<User[]>({
    id,
    selectors: usersSelectors,
    load: useCallback((eventId: string) => users.load(eventId), []),
  });
  const relatedUsers: User[] | null = relatedUsersItem ?? null;

  const resultsCalculating = useSelector<RootState, boolean>(
    (s) => s?.results?.itemsCalculating[id]
  );
  const resultsCalculationError = useSelector<
    RootState,
    StoreError | undefined
  >((s) => s?.results?.itemsCalculationError[id]);

  const tastingEvent = useSelector<RootState, TastingEvent>(
    (s) => s?.events?.items?.find((i) => i.id === id) || ({} as TastingEvent)
  );

  const owner = tastingEvent.owner;
  const canEdit = owner === user?.email;
  const resultsAvailable = Boolean(tastingResults?.lastUpdated);
  const displayLoading = resultsShowLoading || usersShowLoading;

  const handleCalculateResults = () => {
    dispatch(results.calculate(id));
  };

  const handleRetry = () => {
    dispatch(results.loadItem(id));
    dispatch(users.load(id));
  };

  return (
    <Container data-testid="results" disableGutters>
      <Section>
        {(resultsError || usersError) && (
          <ErrorMessage error={resultsError || usersError}>
            <Button color="inherit" onClick={handleRetry}>Retry</Button>
          </ErrorMessage>
        )}
        {displayLoading || resultsCalculating ? (
          <ResultsCircularProgress />
        ) : (
          <ResultsContainer>
            {resultsCalculationError && (
              <ErrorMessage
                error={resultsCalculationError}
              />
            )}
            <TastingResults results={tastingResults} users={relatedUsers} />
            {canEdit && (
              <CalculateResultsButton onClick={handleCalculateResults}>
                {resultsAvailable ? "Rec" : "C"}alculate Results
              </CalculateResultsButton>
            )}
          </ResultsContainer>
        )}
      </Section>
    </Container>
  );
};
export default Results;
